import React, { useEffect, useState } from "react";
import { Sparkles, ShieldCheck, AlertTriangle, ExternalLink, CheckCircle2 } from "lucide-react";
import Spinner from "@/components/Spinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Live AI Net Price panel.
 *
 * Hits POST /api/netprice/live-estimate which has Claude Sonnet 4.5
 * reason step-by-step over: (1) the school's College Scorecard cache,
 * (2) the AidWaive 2026 high-aid registry, (3) the user's residency,
 * income, family size, and number of students currently in college.
 * Surfaces confidence, sources, scholarship programs, recent changes,
 * the FAFSA multi-college callout, and a freshness timestamp.
 *
 * Props (mirrors NetPricePage answers):
 *   school   { id, name, ... full Scorecard detail row }
 *   answers  { residency, income, familySize, studentsInCollege, ... }
 */
export default function LiveAINetPricePanel({ school, answers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!school || !answers) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const body = {
      school_id: String(school.id),
      school_name: school.name,
      residency: answers.residency || "in_state",
      income: Number(answers.income) || 0,
      family_size: Number(answers.familySize) || 1,
      students_in_college: Number(answers.studentsInCollege) || 1,
      school: {
        costOfAttendance: school.costOfAttendance || 0,
        tuitionInState: school.tuitionInState || 0,
        tuitionOutOfState: school.tuitionOutOfState || 0,
        avgNetPrice: school.avgNetPrice || 0,
        netPriceByIncome: school.netPriceByIncome || {},
        ownership: school.ownership || 0,
        type: school.type || "",
      },
    };

    fetch(`${API}/netprice/live-estimate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((j) => { if (!cancelled) { setData(j); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message || "request-failed"); setLoading(false); } });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school?.id, answers?.income, answers?.residency, answers?.familySize, answers?.studentsInCollege]);

  if (loading) {
    return (
      <section
        className="rounded-2xl p-6 sm:p-8 mt-6"
        style={{ background: "#ffffff", border: "1px solid #bfdbfe" }}
        data-testid="live-ai-panel"
      >
        <Eyebrow />
        <Spinner
          variant="block"
          label={`Reasoning live over ${school.name}'s tuition, aid policy, and your specific situation.`}
          testId="live-ai-spinner"
        />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section
        className="rounded-2xl p-6 sm:p-8 mt-6"
        style={{ background: "#ffffff", border: "1px solid #bfdbfe" }}
        data-testid="live-ai-panel"
      >
        <Eyebrow />
        <p className="text-sm text-red-700">
          The live estimator could not return a response right now. The bracket math below still applies.
        </p>
      </section>
    );
  }

  const verified = data.confidence === "verified";
  const fmtUsd = (n) => `$${Number(n || 0).toLocaleString()}`;
  const range =
    data.estimate_low === data.estimate_high
      ? fmtUsd(data.estimate_low)
      : `${fmtUsd(data.estimate_low)} to ${fmtUsd(data.estimate_high)}`;
  const stamp = data.data_freshness_iso
    ? new Date(data.data_freshness_iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <section
      className="rounded-2xl p-6 sm:p-8 mt-6 space-y-6"
      style={{ background: "#ffffff", border: "1px solid #bfdbfe" }}
      data-testid="live-ai-panel"
    >
      <Eyebrow />

      {/* Headline estimate */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-1" data-testid="live-ai-headline-label">
          {data.headline_label || "Estimated annual net cost"}
        </div>
        <div
          className="font-heading leading-[1] text-gray-900"
          style={{ fontSize: "clamp(2.6rem, 5.4vw, 4rem)", letterSpacing: "-0.03em", fontWeight: 335 }}
          data-testid="live-ai-range"
        >
          {range}
        </div>
        {data.includes_note && (
          <p className="text-xs text-gray-500 mt-2" data-testid="live-ai-includes-note">
            {data.includes_note}
          </p>
        )}
        <div
          className={`mt-3 inline-flex items-center gap-2 px-3 h-7 rounded-full text-xs ${
            verified
              ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}
          data-testid="live-ai-confidence"
        >
          {verified ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {data.confidence_label}
        </div>
      </div>

      {/* Summer-work explanation when estimate is under $2,000 */}
      {data.summer_work_note && (
        <div
          className="rounded-xl p-4 text-sm leading-relaxed"
          style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1e40af" }}
          data-testid="live-ai-summer-work"
        >
          {data.summer_work_note}
        </div>
      )}

      {/* Cliff warning for strict-cutoff schools */}
      {data.cliff_warning && (
        <div
          className="rounded-xl p-4 sm:p-5 flex items-start gap-3"
          style={{ background: "#fef2f2", border: "1px solid #fecaca" }}
          data-testid="live-ai-cliff-warning"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 text-red-700 shrink-0" />
          <div>
            <div className="font-semibold text-sm text-red-800 mb-1">Income-cliff warning</div>
            <p className="text-sm leading-relaxed text-red-800">{data.cliff_warning}</p>
          </div>
        </div>
      )}

      {/* Asset caveat — always shown for dataset-matched schools */}
      {data.asset_caveat && (
        <div
          className="rounded-xl p-4 text-sm leading-relaxed"
          style={{ background: "#f9fafb", border: "1px solid #e5e7eb", color: "#374151" }}
          data-testid="live-ai-asset-caveat"
        >
          <span className="font-semibold mr-1">Asset caveat:</span> {data.asset_caveat}
        </div>
      )}

      {/* FAFSA multi-college callout */}
      {data.fafsa_multi_college_note && (
        <div
          className="rounded-xl p-4 sm:p-5 flex items-start gap-3"
          style={{ background: "#fffbeb", border: "1px solid #fcd34d" }}
          data-testid="live-ai-fafsa-callout"
        >
          <span className="text-lg leading-none mt-0.5" aria-hidden>⚠️</span>
          <div>
            <div className="font-semibold text-sm text-amber-900 mb-1">FAFSA Update</div>
            <p className="text-sm leading-relaxed text-amber-900">
              {data.fafsa_multi_college_note}
            </p>
          </div>
        </div>
      )}

      {/* Reasoning steps */}
      {Array.isArray(data.reasoning) && data.reasoning.length > 0 && (
        <div data-testid="live-ai-reasoning">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">
            How the AI arrived at this number
          </div>
          <ol className="text-sm text-gray-800 leading-relaxed space-y-1.5 list-decimal pl-5">
            {data.reasoning.map((step, i) => (
              <li key={i} data-testid={`live-ai-reason-${i}`}>{step}</li>
            ))}
          </ol>
        </div>
      )}

      {/* Scholarship programs */}
      {Array.isArray(data.scholarship_programs) && data.scholarship_programs.length > 0 && (
        <div data-testid="live-ai-scholarships">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">
            Named scholarship programs to look at
          </div>
          <ul className="space-y-1.5">
            {data.scholarship_programs.map((p, i) => (
              <li key={i} className="text-sm" data-testid={`live-ai-scholarship-${i}`}>
                <span className="font-semibold text-gray-900">{p.name}</span>
                <span className="text-gray-600"> · {p.eligibility}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recent changes */}
      {data.recent_changes_12mo && (
        <div
          className="rounded-xl p-4 flex items-start gap-3"
          style={{ background: "#eff6ff", border: "1px solid #bfdbfe" }}
          data-testid="live-ai-recent-changes"
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-700 shrink-0" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-blue-700 mb-1 font-semibold">
              Recent change (last 12 months)
            </div>
            <p className="text-sm text-blue-900">{data.recent_changes_12mo}</p>
          </div>
        </div>
      )}

      {/* Elite-endowment generic educational note. Never cites school-
          specific dollar thresholds; only surfaces when the AI judged
          the Scorecard data supports the narrative. */}
      {data.elite_endowment_note && (
        <div
          className="rounded-xl p-4 text-sm leading-relaxed"
          style={{ background: "#f8fafc", border: "1px solid #e5e7eb", color: "#475569" }}
          data-testid="live-ai-elite-note"
        >
          {data.elite_endowment_note}
        </div>
      )}

      {/* Data warnings */}
      {Array.isArray(data.data_warnings) && data.data_warnings.length > 0 && (
        <div
          className="rounded-xl p-4 space-y-1"
          style={{ background: "#fef2f2", border: "1px solid #fecaca" }}
          data-testid="live-ai-warnings"
        >
          <div className="text-[11px] uppercase tracking-[0.16em] text-red-700 font-semibold mb-1">
            Data integrity notes
          </div>
          {data.data_warnings.map((w, i) => (
            <p key={i} className="text-xs text-red-800" data-testid={`live-ai-warning-${i}`}>{w}</p>
          ))}
        </div>
      )}

      {/* Sources */}
      {Array.isArray(data.sources) && data.sources.length > 0 && (
        <div data-testid="live-ai-sources">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-2">
            Sources cited
          </div>
          <ul className="space-y-1">
            {data.sources.map((src, i) => (
              <li key={i} className="text-xs text-gray-600" data-testid={`live-ai-source-${i}`}>
                {src.url ? (
                  <a
                    href={src.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 underline hover:text-gray-900"
                  >
                    {src.title} <ExternalLink className="w-3 h-3" />
                  </a>
                ) : (
                  <span>{src.title}</span>
                )}
                {src.kind && <span className="text-gray-400"> · {src.kind}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Verify-with-school footer */}
      {data.verify_url && (
        <a
          href={data.verify_url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm underline text-blue-700 hover:text-blue-900"
          data-testid="live-ai-verify-url"
        >
          Verify directly with the school's financial aid office <ExternalLink className="w-3.5 h-3.5" />
        </a>
      )}

      {stamp && (
        <div className="text-[11px] text-gray-400 pt-2 border-t border-gray-100" data-testid="live-ai-timestamp">
          Last updated {stamp}
        </div>
      )}
    </section>
  );
}

function Eyebrow() {
  return (
    <div className="flex items-center gap-2">
      <Sparkles className="w-3.5 h-3.5" style={{ color: "#1d4ed8" }} />
      <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-gray-700">
        Live AI Net Price Estimator
      </span>
    </div>
  );
}
